import { and, assign, setup } from 'xstate';
import { createLocalStorageSync, getSessionStorage, setSessionStorage } from '../../../components/utilities/functions';
import { createItemRelationships } from '../../../components/page/shop/common/create-item-relationships';
import { sentryReporting } from '../../common/sentry';

const cartSync = createLocalStorageSync('cartSession');

const cartActions = ['createRelationships', 'hasStockProduct', 'regionalRestrictions', 'shipsFedex', 'hasPriceChange'];

export const cartMachine = setup({
	guards: {
		hasNoChildren: ({ event }) => event.output?.Cart?.Items?.filter((item) => item?.item_type === 'product_item' && item?.children?.length === 0)?.length > 0,
		calculateTax: ({ context, event }) => context.placement === 'checkout' && event.output?.taxOrigin && !event.output?.error,
	},

	actions: {
		assignDataToContext: assign(({ context, event, self }) => {
			if (process.env.GATSBY_CONTENTSTACK_ENVIRONMENT !== 'production') {
				// eslint-disable-next-line no-console -- development only
				console.log('assignDataToContext - Cart', context, event, self);
			}

			const { output = {}, type, eventError } = event;
			const { data = {}, alert, error } = output;
			const errorMessage = error?.message || data.error?.message || eventError?.message;
			const alertMessage = alert?.message || data.alert?.message;
			const eventType = type.split('.')[3] || type;
			const ignoredMessages = {
				alerts: ['Customer does not have an active cart'],
				errors: ['Customer does not have an active cart'],
			};
			const isValidError = (errorMessage && !ignoredMessages.errors.includes(errorMessage)) || (data?.error && !Object.keys(data.error).length);
			const isValidAlert = alertMessage && !ignoredMessages.alerts.includes(alertMessage);

			if (isValidError) {
				sentryReporting(event, self);
			}

			if (isValidError || isValidAlert) {
				const message = errorMessage || alertMessage || 'An unknown error occurred';
				return {
					cartErrors: {
						...context.cartErrors,
						[eventType]: message,
					},
				};
			}

			const cartEvents = ['getCart', 'updateCartItem', 'deleteCartItem', 'addBundleToCart', 'addPromotion', 'deletePromotion'];
			if (cartEvents.includes(eventType)) {
				const cart = output.Cart;

				cartSync.set(cart);

				return {
					cart: cart,
					cartErrors: {},
				};
			} else if (eventType === 'getBundle') {
				return {
					bundlePrice: data?.price,
				};
			} else if (eventType === 'PROMO_CODE_ENTRY') {
				let updatedCartErrors = { ...context.cartErrors };
				if (context.cartErrors.addPromotion) {
					delete updatedCartErrors.addPromotion;
				}

				return {
					promoCode: event?.promoCode,
					cartErrors: { ...updatedCartErrors },
				};
			} else if (eventType === 'PRO_CHANGE') {
				return {
					isPro: event?.isPro,
					proPricingModalOpen: event?.isPro,
				};
			} else if (eventType === 'copyProjectItemToQuote') {
				if (output.data.Cart?.total_number > context.cart?.total_number) {
					const arr = getSessionStorage('projectItemsAdded') || [];
					arr.push(context.copiedItem);
					setSessionStorage('projectItemsAdded', arr);
				}

				return {
					cart: output.data.Cart,
					copiedItem: null,
				};
			} else if (eventType === 'getTaxEstimate') {
				return {
					tax: output.Tax,
				};
			} else {
				sentryReporting(event, self);

				return {
					cartErrors: {
						...context.cartErrors,
						[eventType]: errorMessage || 'An unknown error occurred',
					},
				};
			}
		}),

		copiedItem: assign({
			copiedItem: ({ event }) => event?.item_id,
		}),

		shipsFedex: assign(({ context }) => {
			if (context.placement !== 'checkout') return;

			return {
				shipsFedex: context?.cart?.Items?.filter((item) => item?.item_type === 'product_item')?.length === 0,
			};
		}),

		hasStockProduct: assign(({ context }) => {
			if (context.placement !== 'checkout') return;

			return {
				hasStockProduct: context?.cart?.Items?.filter((item) => item?.item_type === 'product_item' && item?.product_type === 'stock-window')?.length > 0,
			};
		}),

		toggleSigninModal: assign({
			signinModalOpen: ({ context }) => !context.signinModalOpen,
		}),

		toggleProPricingModal: assign({
			proPricingModalOpen: ({ context }) => !context.proPricingModalOpen,
		}),

		toggleOrderReview: assign({
			orderReviewOpen: ({ context }) => !context.orderReviewOpen,
		}),

		assignBranchRegion: assign({
			branchRegion: ({ event }) => event.params.branchRegion,
		}),

		regionalRestrictions: assign(({ context, event }) => {
			if (!['cart', 'checkout'].includes(context.placement)) return;

			const westCoastEncompassDHRestriction =
				context.cart?.Items?.some((item) => {
					const backendName = item.backend_name.toLowerCase();
					return backendName.includes('encompass') && (backendName.includes('double-hung') || backendName.includes('dhung'));
				}) && (event.branchRegion ? event.branchRegion === 'west' : context.branchRegion === 'west');

			//TODO: investigate if westCoastEncompassDHRestriction is no longer necessary with the new -w suffix restriction

			const eastWestSuffixRestriction = context.cart?.Items?.some((item) => {
				const backendName = item.backend_name.toLowerCase();

				if (backendName?.includes('encompass')) {
					if (/-w$/.test(backendName)) {
						return event.branchRegion ? event.branchRegion === 'east' : context.branchRegion === 'east';
					} else {
						return event.branchRegion ? event.branchRegion === 'west' : context.branchRegion === 'west';
					}
				}
			});

			return {
				branchRegion: event.branchRegion || context.branchRegion,
				cartErrors: {
					...context.cartErrors,
					...(westCoastEncompassDHRestriction ? { westCoastEncompassDHRestriction: true } : {}),
					...(eastWestSuffixRestriction ? { eastWestSuffixRestriction: true } : {}),
				},
			};
		}),

		hasPriceChange: assign(({ context }) => {
			const hasPriceChange = context.cart?.Items?.some((item) => item?.unit_price_change?.type !== 'equal') ? true : false;

			return {
				hasPriceChange,
			};
		}),

		createRelationships: assign(({ context }) => {
			if (context.placement !== 'cart') return;

			const cart = context.cart;
			let updatedCart;

			if (cart?.Items) {
				const { view, summaryTotals } = createItemRelationships(cart.Items);
				updatedCart = { ...cart, Items: view, summary: [...summaryTotals.values()] };
			}

			return {
				cart: updatedCart,
			};
		}),

		dataLayerUpdate: ({ context, event }) => {
			const eventType = event.type.split('.')[3] || event.type;
			window.dataLayer = window.dataLayer || [];

			if (eventType === 'addPromotion') {
				window.dataLayer.push({
					event: 'couponApply',
					ecommerce: {
						couponApplied: context.promoCode,
						couponSuccess: !event.output.error,
					},
				});
			}

			if (event.type === 'DELETE_PROMOTION') {
				window.dataLayer.push({
					event: 'couponRemove',
					ecommerce: {
						couponRemoved: event.promoCode,
					},
				});
			}

			if (eventType === 'DELETE_ITEM') {
				const { specifications, item_url, unit_price, quantity, product_name } = event.item;

				const dataLayerItem = specifications.reduce((acc, question) => {
					const key = question.key.charAt(0).toLowerCase() + question.key.split('/')[0].slice(1).replace(/\s+/g, '');
					acc[key] = question.value;
					return acc;
				}, {});
				const itemId = new URL(item_url, window.location.origin).searchParams.get('id');
				const price = (unit_price.amount / 100).toFixed(2);

				const dataLayerPayload = {
					event: 'removeFromCart',
					ecommerce: {
						remove: {
							products: [
								{
									id: itemId,
									currency: 'USD',
									price,
									quantity,
									name: itemId,
									category: product_name,
									...dataLayerItem,
								},
							],
						},
					},
				};

				window.dataLayer.push(dataLayerPayload);
			}
		},
	},
}).createMachine({
	id: 'cart',
	initial: 'idle',
	context: ({ input }) => {
		const { branchRegion, postalCode, placement, isPro, cart, proPricingModalOpen } = input || {};
		return {
			branchRegion: branchRegion,
			postalCode: postalCode,
			placement: placement,
			isPro: isPro,
			cart: cart,
			cartErrors: {},
			bundlePrice: {},
			tax: {},
			signinModalOpen: false,
			hasStockProduct: false,
			orderReviewOpen: false,
			shipsFedex: false,
			proPricingModalOpen: proPricingModalOpen,
		};
	},
	on: {
		PRO_CHANGE: { actions: 'assignDataToContext' },
	},
	states: {
		idle: {
			on: {
				GET_CART: {
					target: 'getCart',
					actions: 'assignBranchRegion',
				},
				UPDATE_ITEM_QUANTITY: 'updateCartItem',
				DELETE_ITEM: 'deleteCartItem',
				ADD_INSTALLATION_KIT: 'addBundleToCart',
				PROMO_CODE_ENTRY: { actions: 'assignDataToContext' },
				ADD_PROMOTION: 'addPromotion',
				DELETE_PROMOTION: 'deletePromotion',
				TOGGLE_SIGNIN_MODAL: { actions: 'toggleSigninModal' },
				TOGGLE_PRO_PRICING_MODAL: { actions: 'toggleProPricingModal' },
				COPY_PROJECT_ITEM_TO_QUOTE: 'copyProjectItemToQuote',
				TOGGLE_ORDER_REVIEW: { actions: 'toggleOrderReview' },
				CALCULATE_TAX: 'getTaxEstimate',
			},
		},

		getCart: {
			invoke: {
				id: 'getCart',
				src: 'getCart',
				onDone: [
					{
						target: 'getBundle',
						guard: 'hasNoChildren',
						actions: cartActions,
					},
					{
						target: 'idle',
						actions: cartActions,
					},
				],
				onError: {
					target: 'idle',
				},
			},
			exit: ['assignDataToContext'],
		},

		getBundle: {
			invoke: {
				id: 'getBundle',
				src: 'getBundle',
				onDone: {
					target: 'idle',
				},
				onError: {
					target: 'idle',
				},
			},
			exit: ['assignDataToContext'],
		},

		updateCartItem: {
			invoke: {
				id: 'updateCartItem',
				src: 'updateCartItem',
				input: ({ event }) => {
					return event;
				},
				onDone: {
					target: 'idle',
					actions: ['createRelationships'],
				},
				onError: {
					target: 'idle',
				},
			},
			exit: ['assignDataToContext'],
		},

		deleteCartItem: {
			invoke: {
				id: 'deleteCartItem',
				src: 'deleteCartItem',
				input: ({ event }) => {
					return event;
				},
				onDone: [
					{
						target: 'getBundle',
						guard: and(['hasNoChildren', ({ context }) => ['cart'].includes(context.placement)]),
						actions: cartActions,
					},
					{
						target: 'idle',
						actions: cartActions,
					},
				],
				onError: {
					target: 'idle',
				},
			},
			entry: ['dataLayerUpdate'],
			exit: ['assignDataToContext'],
		},

		addBundleToCart: {
			invoke: {
				id: 'addBundleToCart',
				src: 'addBundleToCart',
				input: ({ event }) => {
					return event;
				},
				onDone: [
					{
						target: 'idle',
						actions: ['createRelationships'],
					},
				],
				onError: {
					target: 'idle',
				},
			},
			exit: ['assignDataToContext'],
		},

		addPromotion: {
			invoke: {
				id: 'addPromotion',
				src: 'addPromotion',
				input: ({ event }) => {
					return event;
				},
				onDone: [
					{
						guard: 'calculateTax',
						target: 'getTaxEstimate',
						actions: ['createRelationships'],
					},
					{
						guard: ({ context, event }) => context.placement === 'cart' && !event.output?.error,
						target: 'idle',
						actions: ['createRelationships'],
					},
					{
						target: 'idle',
					},
				],
				onError: {
					target: 'idle',
				},
			},
			exit: ['assignDataToContext', 'dataLayerUpdate'],
		},

		deletePromotion: {
			invoke: {
				id: 'deletePromotion',
				src: 'deletePromotion',
				input: ({ event }) => {
					return event;
				},
				onDone: [
					{
						guard: 'calculateTax',
						target: 'getTaxEstimate',
						actions: ['createRelationships'],
					},
					{
						target: 'idle',
						actions: ['createRelationships'],
					},
				],
				onError: {
					target: 'idle',
				},
			},
			entry: ['dataLayerUpdate'],
			exit: ['assignDataToContext'],
		},

		copyProjectItemToQuote: {
			invoke: {
				id: 'copyProjectItemToQuote',
				src: 'copyProjectItemToQuote',
				input: ({ event }) => {
					return event;
				},
				onDone: {
					target: 'idle',
					actions: ['createRelationships'],
				},
				onError: {
					target: 'idle',
				},
			},
			entry: ['copiedItem'],
			exit: ['assignDataToContext'],
		},

		getTaxEstimate: {
			invoke: {
				id: 'getTaxEstimate',
				src: 'getTaxEstimate',
				input: ({ event }) => {
					return event;
				},
				onDone: {
					target: 'idle',
				},
				onError: {
					target: 'idle',
				},
			},
			exit: ['assignDataToContext'],
		},
	},
});
